import { apiClient } from '../config/api';

// FEAT-047 — State agency mappings client (web).
// MVP-8.16, 8.17, 8.18, 8.19, 8.35, 8.35a, 8.35b, 8.35c, 18.19, 18.41.
//
// The web bundle never reads `verifyUrlTemplate` directly — instead the
// `getVerifyUrl()` call asks the server to substitute the license number
// safely. The public list/detail endpoints only expose
// `{ stateCode, agencyLabel, registerUrl }`.

export interface PublicStateAgencyMapping {
  stateCode: string;
  agencyLabel: string;
  registerUrl: string;
}

export interface StateAgencyVerifyUrlResponse {
  stateCode: string;
  agencyLabel: string;
  verifyUrl: string;
}

interface ListResponse {
  mappings: PublicStateAgencyMapping[];
}

interface DetailResponse {
  mapping: PublicStateAgencyMapping;
}

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.message.includes('status: 404');
}

function isBadRequestError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.message.includes('status: 400');
}

class StateAgencyMappingService {
  async list(): Promise<PublicStateAgencyMapping[]> {
    const response = await apiClient.get<ListResponse>('/state-agency-mappings');
    return response.data.mappings ?? [];
  }

  async getByStateCode(stateCode: string): Promise<PublicStateAgencyMapping | null> {
    if (!stateCode) return null;
    try {
      const response = await apiClient.get<DetailResponse>(
        `/state-agency-mappings/${encodeURIComponent(stateCode)}`,
      );
      return response.data?.mapping ?? null;
    } catch (error) {
      if (isNotFoundError(error)) return null;
      throw error;
    }
  }

  async getVerifyUrl(
    stateCode: string,
    licenseNumber: string,
  ): Promise<StateAgencyVerifyUrlResponse | null> {
    if (!stateCode || !licenseNumber) return null;
    try {
      const response = await apiClient.get<StateAgencyVerifyUrlResponse>(
        `/state-agency-mappings/${encodeURIComponent(stateCode)}/verify-url?licenseNumber=${encodeURIComponent(licenseNumber)}`,
      );
      return response.data;
    } catch (error) {
      if (isNotFoundError(error) || isBadRequestError(error)) return null;
      throw error;
    }
  }
}

export const stateAgencyMappingService = new StateAgencyMappingService();
